// ON-407: web signup must produce the same account as the app's SignUpScreen
// (stazon-app src/screens/SignUpScreen.tsx) — same fields, validation and
// user_metadata keys. Keep the two in step.

export const TERMS_URL = 'https://stazon.cc/terms-and-conditions'
export const PRIVACY_URL = 'https://stazon.cc/privacy-policy'

export interface SignupFields {
  fullName: string
  email: string
  password: string
  confirmPassword: string
  acceptedTerms: boolean
  acceptedPrivacy: boolean
}

/** Keys into STRINGS[lang].rsvp.validation; same names as the app's `validation.*`. */
export type SignupErrorKey =
  | 'fullNameRequired'
  | 'emailRequired'
  | 'invalidEmail'
  | 'passwordRequirements'
  | 'passwordsNoMatch'
  | 'acceptTerms'
  | 'acceptPrivacy'

export type SignupErrors = Partial<
  Record<'fullName' | 'email' | 'password' | 'confirmPassword' | 'terms' | 'privacy', SignupErrorKey>
>

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** RUN-12: min 8 chars, must include a letter and a number. */
export function validateSignupPassword(password: string) {
  const hasMinLength = password.length >= 8
  const hasLetter = /[a-zA-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  return { isValid: hasMinLength && hasLetter && hasNumber, hasMinLength, hasLetter, hasNumber }
}

export function validateSignupForm(f: SignupFields): SignupErrors {
  const errors: SignupErrors = {}
  if (!f.fullName.trim()) errors.fullName = 'fullNameRequired'
  if (!f.email.trim()) errors.email = 'emailRequired'
  else if (!EMAIL_RE.test(f.email.trim())) errors.email = 'invalidEmail'
  if (!validateSignupPassword(f.password).isValid) errors.password = 'passwordRequirements'
  if (!(f.password.length > 0 && f.password === f.confirmPassword)) errors.confirmPassword = 'passwordsNoMatch'
  if (!f.acceptedTerms) errors.terms = 'acceptTerms'
  if (!f.acceptedPrivacy) errors.privacy = 'acceptPrivacy'
  return errors
}

export function consentMetadata(now: Date = new Date()) {
  const at = now.toISOString()
  return { accepted_terms_at: at, accepted_privacy_at: at }
}

/** `options.data` for supabase.auth.signUp — identical keys to the app. */
export function signupMetadata(fullName: string, now: Date = new Date()) {
  return { full_name: fullName.trim(), ...consentMetadata(now) }
}

/**
 * True when the signed-in user has no recorded terms + privacy acceptance —
 * e.g. an account first created through Google OAuth, which never passes
 * through the signup form.
 */
export function needsTermsAcceptance(user: { user_metadata?: Record<string, unknown> } | null | undefined): boolean {
  const meta = user?.user_metadata ?? {}
  return !meta.accepted_terms_at || !meta.accepted_privacy_at
}

/** signUp's anti-enumeration response for an already-registered email. */
export function isObfuscatedExistingUser(user: { identities?: unknown[] } | null | undefined): boolean {
  return !!user && Array.isArray(user.identities) && user.identities.length === 0
}
